<template>
  <div class="relative">
    <div @click="toggle">
      <slot name="trigger" :open="isOpen" :toggle="toggle" />
    </div>
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="absolute left-0 top-full mt-2 z-50 w-64 bg-white border border-neutral-200 rounded-lg shadow-lg py-2 max-h-[70vh] overflow-y-auto"
    >
      <template v-for="section in dropdownSections" :key="section.title">
        <div class="px-3 py-1.5 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
          {{ section.title }}
        </div>
        <!-- Группа модулей -->
        <template v-for="(item, idx) in section.items" :key="item.type === 'group' ? 'g-' + item.id : 'm-' + idx">
          <template v-if="item.type === 'group'">
            <div class="px-3 py-1 text-xs text-neutral-400">{{ item.name }}</div>
            <router-link
              v-for="module in item.modules"
              :key="'gm-' + module.id"
              :to="getModuleHref(module)"
              class="flex items-center gap-2 px-3 py-2 pl-5 text-sm text-neutral-900 hover:bg-neutral-50 transition-colors"
              @click="handleModuleClick(module)"
            >
              <component :is="getIconComponent(module.key)" class="w-5 h-5 shrink-0" />
              <span>{{ module.name }}</span>
            </router-link>
          </template>
          <!-- Обычный модуль -->
          <a
            v-else-if="item.module?.key === 'logout'"
            href="#"
            class="flex items-center gap-2 px-3 py-2 text-sm text-neutral-900 hover:bg-neutral-50 transition-colors"
            @click.prevent="handleModuleClick(item.module)"
          >
            <component :is="getIconComponent(item.module?.key)" class="w-5 h-5 shrink-0" />
            <span>{{ item.module?.name }}</span>
          </a>
          <router-link
            v-else
            :to="getModuleHref(item.module)"
            class="flex items-center gap-2 px-3 py-2 text-sm text-neutral-900 hover:bg-neutral-50 transition-colors"
            @click="handleModuleClick(item.module)"
          >
            <component :is="getIconComponent(item.module?.key)" class="w-5 h-5 shrink-0" />
            <span>{{ item.module?.name }}</span>
          </router-link>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth';
import DeskIcon from '@/components/navigationIcons/DeskIcon.vue';
import BookIcon from '@/components/navigationIcons/BookIcon.vue';
import StorehouseIcon from '@/components/navigationIcons/StorehouseIcon.vue';
import OrdersIcon from '@/components/navigationIcons/OrdersIcon.vue';
import DefaultIcon from '@/components/navigationIcons/DefaultIcon.vue';
import ProductionIcon from '@/components/navigationIcons/ProductionIcon.vue';
import ProfileIcon from '@/components/navigationIcons/ProfileIcon.vue';
import LogoutIcon from '@/components/navigationIcons/LogoutIcon.vue';
import ReceiptInvoiceControlIcon from '@/components/navigationIcons/ReceiptInvoiceControlIcon.vue';
import SettingsIcon from '@/components/navigationIcons/SettingsIcon.vue';

const DESK_MODULE_KEYS = ['assembler-desk', 'picking-desk', 'otk-desk', 'packer-desk', 'storekeeper-desk', 'smk-desk'];

export default {
  name: 'NavigationDropdown',
  components: {
    DeskIcon,
    BookIcon,
    StorehouseIcon,
    OrdersIcon,
    DefaultIcon,
    ProductionIcon,
    ProfileIcon,
    LogoutIcon,
    ReceiptInvoiceControlIcon,
    SettingsIcon,
  },
  emits: ['select'],
  data() {
    return {
      isOpen: false,
      clickOutsideHandler: null,
    };
  },
  computed: {
    user() {
      return useAuthStore()?.user;
    },
    dropdownSections() {
      const currentUser = this.user;
      if (!currentUser?.modules) return [];

      const deskModules = currentUser.modules.filter((m) => DESK_MODULE_KEYS.includes(m.key)) || [];
      const otherModules = currentUser.modules.filter((m) => !DESK_MODULE_KEYS.includes(m.key)) || [];

      // Разделы администрирования: ключ раздела — App\Enums\AdminSections,
      // пункт показывается, если роль даёт доступ к разделу (полный или чтение)
      const adminSections = [
        { name: 'Номенклатура', route: 'products', section: 'products' },
        { name: 'Контрагенты', route: 'companies', section: 'companies' },
        { name: 'Пользователи', route: 'users', section: 'users' },
        { name: 'Модули', route: 'modules', section: 'modules' },
        { name: 'Цеха', route: 'workshops', section: 'workshops' },
        { name: 'Рабочие центры', route: 'workshopPostUnits', section: 'workshop_posts' },
        { name: 'Виды рабочих центров', route: 'workshopPosts', section: 'workshop_post_types' },
        { name: 'Страны', route: 'countries', section: 'countries' },
        { name: 'Организации', route: 'organizations', section: 'organizations' },
        { name: 'Договоры', route: 'contracts', section: 'contracts' },
        { name: 'Ставки НДС', route: 'vat-rates', section: 'vat_rates' },
        { name: 'Заказы поставщику', route: 'supplier-orders', section: 'supplier_orders' },
        { name: 'Отчёты ПСИ и ВХК', route: 'otk-reports', section: 'otk_reports' },
        { name: 'Роли', route: 'roles', section: 'roles' },
      ];

      const authStore = useAuthStore();
      const adminModules = adminSections
        .filter((item) => authStore.canSection(item.section))
        .map((item) => ({ name: item.name, route: item.route, key: 'admin' }));

      if (currentUser?.email === 'admin@admin.ru') {
        adminModules.push({ name: 'Запросы 1С', route: 'onecRequests', key: 'admin' });
        adminModules.push({ name: 'Резервные копии', route: 'admin/backup', key: 'backup' });
        adminModules.push({ name: 'Единицы продукции', route: 'batch-products', key: 'admin' });
      }

      const settingsModules = [{ name: 'Настройки навигации', route: 'navigation-settings', key: 'settings' }];
      const commonModules = [
        { name: 'Профиль', route: 'user', key: 'profile' },
        { name: 'Выйти', route: null, key: 'logout' },
      ];

      const grouped = {};
      const ungrouped = [];
      otherModules.forEach((m) => {
        if (m.module_group_id && m.module_group) {
          const gid = m.module_group_id;
          if (!grouped[gid]) {
            grouped[gid] = {
              type: 'group',
              id: gid,
              name: m.module_group.name,
              key: m.module_group.key || m.key,
              modules: [],
            };
          }
          grouped[gid].modules.push(m);
        } else {
          ungrouped.push({ type: 'module', module: m });
        }
      });

      Object.values(grouped).forEach((group) => {
        group.modules.sort((a, b) => {
          const keyCompare = (a.key || '').localeCompare(b.key || '');
          if (keyCompare !== 0) return keyCompare;
          return (a.name || '').localeCompare(b.name || '');
        });
      });

      const allItems = [...Object.values(grouped), ...ungrouped].sort((a, b) => {
        const aKey = a.type === 'group' ? a.key || '' : a.module?.key || '';
        const bKey = b.type === 'group' ? b.key || '' : b.module?.key || '';
        const keyCompare = aKey.localeCompare(bKey);
        if (keyCompare !== 0) return keyCompare;
        const aName = a.type === 'group' ? a.name || '' : a.module?.name || '';
        const bName = b.type === 'group' ? b.name || '' : b.module?.name || '';
        return aName.localeCompare(bName);
      });

      const sections = [];
      if (deskModules.length) {
        sections.push({ title: 'Рабочие модули', items: deskModules.map((m) => ({ type: 'module', module: m })) });
      }
      if (allItems.length) {
        sections.push({ title: 'Остальные модули', items: allItems });
      }
      if (adminModules.length) {
        sections.push({ title: 'Администрирование', items: adminModules.map((m) => ({ type: 'module', module: m })) });
      }
      if (currentUser?.email === 'admin@admin.ru') {
        sections.push({ title: 'Настройки', items: settingsModules.map((m) => ({ type: 'module', module: m })) });
      }
      sections.push({ title: 'Общее', items: commonModules.map((m) => ({ type: 'module', module: m })) });

      return sections;
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.clickOutsideHandler = (e) => {
          if (this.$el && !this.$el.contains(e.target)) {
            this.close();
          }
        };
        setTimeout(() => document.addEventListener('click', this.clickOutsideHandler), 0);
      } else {
        if (this.clickOutsideHandler) {
          document.removeEventListener('click', this.clickOutsideHandler);
          this.clickOutsideHandler = null;
        }
      }
    },
  },
  beforeUnmount() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    close() {
      this.isOpen = false;
    },
    getModuleHref(module) {
      if (!module?.route) return { name: 'Home' };
      // route уже содержит id для desk-модулей (формат: "picking-desk/6" из UserModulesResource)
      return `/${module.route}`;
    },
    handleModuleClick(module) {
      this.close();
      this.$emit('select', module);
      if (module?.key === 'logout') {
        useAuthStore()
          .logout()
          .then(() => {
            window.location.href = '/login';
          })
          .catch((error) => {
            console.error('Ошибка при выходе из системы:', error);
            alert('Произошла ошибка при выходе из системы');
          });
      }
    },
    getIconComponent(key) {
      switch (key) {
        case 'assembler-desk':
        case 'picking-desk':
        case 'otk-desk':
        case 'packer-desk':
        case 'storekeeper-desk':
        case 'smk-desk':
          return 'DeskIcon';
        case 'storehouse':
          return 'StorehouseIcon';
        case 'orders':
          return 'OrdersIcon';
        case 'production':
          return 'ProductionIcon';
        case 'receipt-invoice-control':
          return 'ReceiptInvoiceControlIcon';
        case 'profile':
          return 'ProfileIcon';
        case 'logout':
          return 'LogoutIcon';
        case 'settings':
          return 'SettingsIcon';
        default:
          return 'DefaultIcon';
      }
    },
  },
};
</script>
